import React, { useState, useMemo, useRef } from 'react';
import { useAuthNavigation } from '../shared/navigation/useAuthNavigation';
import { View, Text, ScrollView } from 'react-native';
import { SvgIcon } from '../components/common/SvgIcon';
import { Clickable2 } from '../components/common/Clickable2';
import { ButtonLoader } from '../components/load-main/ButtonLoader';
import { Input } from '../components/common/Input';

import { useAuthStore } from '../shared/auth/store';
import { appTheme, useThemeColors } from '../shared/theme';
import { updatePassword } from '../data/domains/profile';
import { useTranslation } from '../shared/i18n';
import { showErrorAlert, showSuccessAlert } from '../components/appAlerts';
import { useAsyncWithLoading } from '../components/ui/hooks/useAsyncWithLoading';
import { sharedStyles, buttonWrap, createFrostedPrimaryGlassLabelStyle, createFrostedPrimaryGlassSurfaceStyle } from '../shared/theme/sharedStyles';
import { createPasswordRecoveryFormSheetStyles } from '../shared/theme/authSheetStyles';
import { authSheetFormSpacing, AUTH_FORM_KEYBOARD, useAuthFormScrollMaxHeight } from '../shared/auth';
import { useAuthInputKeyboardAvoidance } from './keyboard/useAuthInputKeyboardAvoidance';
import { AuthFocusTarget } from './keyboard/AuthFocusTarget';

export type ResetPasswordSheetContentProps = {
  onDismiss: () => void;
  routeParams: Record<string, unknown>;
  /** Extra auth modal lift when scroll cannot clear the focused field (overlap only, never full keyboard height). */
  onKeyboardPanelNudge?: (offset_px: number) => void;
};

export function ResetPasswordSheetContent({ onDismiss, routeParams: params, onKeyboardPanelNudge }: ResetPasswordSheetContentProps) {
  const navigation = useAuthNavigation();
  const is_authenticated = useAuthStore(s => s.isAuthenticated);
  const { t } = useTranslation();

  const colors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createPasswordRecoveryFormSheetStyles(colors), [colors]);
  const scroll_max_h = useAuthFormScrollMaxHeight();

  const phoneNo = (params.phoneNo as string) || '';
  const countryIsoCode = params.country_iso_code ?? 'KW';

  const [resetToken, setResetToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, runAsync] = useAsyncWithLoading();

  const validateForm = (): boolean => {
    if (!resetToken || resetToken.length < 6) {
      showErrorAlert(t, { message: t('auth.enterValidResetCode') });
      return false;
    }
    if (!password || password.length < 6) {
      showErrorAlert(t, { message: t('auth.passwordMinLength') });
      return false;
    }
    if (password !== confirmPassword) {
      showErrorAlert(t, { message: t('auth.passwordsDoNotMatch') });
      return false;
    }
    return true;
  };

  const handleResetPassword = async () => {
    if (!validateForm()) {
      return;
    }

    await runAsync(async () => {
      const response: { success?: boolean; message?: string } = await updatePassword({
        phone: phoneNo,
        country_iso_code: String(countryIsoCode),
        reset_password_token: resetToken,
        password: password,
        password_confirmation: confirmPassword,
        otp_confirmed: true,
      });

      if (response.success) {
        showSuccessAlert(t, {
          message: response.message || t('auth.passwordResetSuccess'),
          buttons: [
            {
              text: t('common.ok'),
              onPress: () => {
                onDismiss();
                if (is_authenticated) {
                  (navigation as { navigate: (a: string, p?: object) => void }).navigate('MainTabs', { screen: 'Dashboard' });
                } else {
                  (navigation as { navigate: (a: string, p?: object) => void }).navigate('Main');
                }
              },
            },
          ],
        });
      } else {
        showErrorAlert(t, { message: response.message || t('auth.passwordResetFailedMessage') });
      }
    }).catch((error: { error?: { message?: string; password?: string[] } } | Error) => {
      const errMsg =
        'error' in (error as object) && (error as { error?: { message?: string; password?: string[] } }).error
          ? (error as { error: { message?: string; password?: string[] } }).error.message ||
            (error as { error: { password?: string[] } }).error.password?.[0]
          : error instanceof Error
            ? error.message
            : t('auth.unexpectedError');
      showErrorAlert(t, { message: errMsg || t('auth.unexpectedError') });
    });
  };

  const scroll_ref = useRef<ScrollView>(null);
  const scroll_y = useRef(0);
  const token_block_ref = useRef<View>(null);
  const password_block_ref = useRef<View>(null);
  const confirm_block_ref = useRef<View>(null);
  const { on_field_focus, on_scroll_layout, on_scroll_content_size_change, keyboard_scroll_pad } = useAuthInputKeyboardAvoidance({
    enabled: true,
    scroll_ref,
    scroll_y_ref: scroll_y,
    on_panel_offset: onKeyboardPanelNudge,
    clearance_px: AUTH_FORM_KEYBOARD.fieldFocusClearancePx,
  });

  const frosted_primary = useMemo(
    () => ({
      surface: createFrostedPrimaryGlassSurfaceStyle(colors),
      label: createFrostedPrimaryGlassLabelStyle(colors),
    }),
    [colors],
  );

  return (
    <View style={styles.keyboardView}>
      <ScrollView
        ref={scroll_ref}
        onScroll={e => {
          scroll_y.current = e.nativeEvent.contentOffset.y;
        }}
        onLayout={on_scroll_layout}
        onContentSizeChange={on_scroll_content_size_change}
        scrollEventThrottle={16}
        style={[styles.scroll, { maxHeight: scroll_max_h }]}
        contentContainerStyle={[
          styles.scrollContent,
          {
            paddingTop: authSheetFormSpacing.formPaddingTop,
            paddingBottom: authSheetFormSpacing.formPaddingBottom + keyboard_scroll_pad,
          },
        ]}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="on-drag"
        automaticallyAdjustKeyboardInsets={false}
        bounces={false}
      >
        <View style={styles.iconContainer}>
          <SvgIcon name="lock" size={48} color={colors.primary} />
        </View>

        <Text style={styles.title}>{t('auth.createNewPassword')}</Text>
        <Text style={styles.subtitle}>{t('auth.resetPasswordSubtitle')}</Text>

        <AuthFocusTarget ref={token_block_ref} style={styles.formGroup}>
          <Input
            value={resetToken}
            onChangeText={setResetToken}
            placeholder={t('auth.resetCodePlaceholder')}
            keyboardType="number-pad"
            maxLength={6}
            onFocus={() => on_field_focus(token_block_ref)}
          />
        </AuthFocusTarget>

        <AuthFocusTarget ref={password_block_ref} style={styles.formGroup}>
          <Input
            value={password}
            onChangeText={setPassword}
            placeholder={t('auth.newPasswordPlaceholder')}
            secureTextEntry
            onFocus={() => on_field_focus(password_block_ref)}
          />
        </AuthFocusTarget>

        <AuthFocusTarget ref={confirm_block_ref} style={styles.formGroup}>
          <Input
            value={confirmPassword}
            onChangeText={setConfirmPassword}
            placeholder={t('auth.confirmPasswordPlaceholder')}
            secureTextEntry
            onFocus={() => on_field_focus(confirm_block_ref)}
          />
        </AuthFocusTarget>

        {password && confirmPassword && password !== confirmPassword ? <Text style={styles.errorText}>{t('auth.passwordsDoNotMatch')}</Text> : null}

        <View style={{ marginTop: authSheetFormSpacing.beforePrimaryActions }}>
          <View style={sharedStyles.buttonStack}>
            <Clickable2
              onPress={handleResetPassword}
              disabled={loading}
              style={{ alignSelf: 'stretch', flexShrink: 0 }}
              accessibilityLabel={t('auth.resetButton')}
              accessibilityRole="button"
              accessibilityState={{ disabled: loading }}
            >
              <View style={[buttonWrap, { shadowColor: colors.shadowColor }]}>
                <View style={frosted_primary.surface}>
                  {loading ? (
                    <ButtonLoader size={16} color={colors.onPrimary ?? colors.primaryText ?? colors.white} />
                  ) : (
                    <Text style={frosted_primary.label}>{t('auth.resetButton')}</Text>
                  )}
                </View>
              </View>
            </Clickable2>
          </View>
        </View>

        <View style={styles.scrollBottomSpacer} />
      </ScrollView>
    </View>
  );
}

export default ResetPasswordSheetContent;
